import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { type ClassificationCategoryCandidate, parseClassificationResult, useAiSuggestionApi } from "@/api/ai-suggestions";
import { useCategoryApi } from "@/api/categories";
import { type TicketDetailDto, type TicketDto, type TicketPriority, TicketStatus, useTicketApi } from "@/api/tickets";
import { Permissions } from "@/auth/permissions";
import { usePermission } from "@/auth/session-provider";
import { CategoryTreeSelect } from "@/components/category-tree-select";
import { ExtendedInfoList, useExtendedInfo } from "@/components/extended-info-list";
import { Icon } from "@/components/icon";
import { Loader } from "@/components/motion/loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import { formatDateTime, priorityPresentation, statusPresentation, TICKET_PRIORITY_VALUES } from "../utils/ticket-presentation";
import { AiCategoryLabelAction, type AppliedAiCategoryFeedback, TicketAiClassification, useAiProgressMessage } from "./ticket-ai-classification";
import { TicketAssignmentDialog } from "./ticket-assignment-dialog";
import { TicketFollowUpAssignment } from "./ticket-follow-up-assignment";
import { TicketPropertiesGrid, TicketPropertiesGridRow } from "./ticket-properties-grid";
import { TicketPropertiesSection } from "./ticket-properties-section";

type TicketPropertiesProps = {
  customerDetailHref?: string;
  ticket: TicketDetailDto;
  onResolve: () => void;
};

type OptimisticTicketContext = {
  previousTicket?: TicketDetailDto;
};

const MAX_VISIBLE_PARTICIPANTS = 5;
const PROPERTY_SELECT_TRIGGER_CLASS =
  "h-10 w-full border-transparent bg-muted/70 px-3 hover:bg-muted focus-visible:border-ring disabled:opacity-100 dark:bg-muted/70 dark:hover:bg-muted";

const STATUS_TRANSITIONS: Record<number, TicketStatus[]> = {
  [TicketStatus.New]: [TicketStatus.Cancelled],
  [TicketStatus.Open]: [TicketStatus.Pending, TicketStatus.Resolved, TicketStatus.Cancelled],
  [TicketStatus.Pending]: [TicketStatus.Open],
  [TicketStatus.Resolved]: [TicketStatus.Open, TicketStatus.Closed],
  [TicketStatus.Closed]: [TicketStatus.Open],
  [TicketStatus.Cancelled]: [],
};

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function TicketProperties({ customerDetailHref, ticket, onResolve }: TicketPropertiesProps) {
  const { intlLocale, t } = useI18n();
  const queryClient = useQueryClient();
  const ticketApi = useTicketApi();
  const categoryApi = useCategoryApi();
  const aiApi = useAiSuggestionApi();
  const { can } = usePermission();
  const extendedInfo = useExtendedInfo(ticket.extendedInfoJson);
  const [appliedAiFeedback, setAppliedAiFeedback] = useState<AppliedAiCategoryFeedback>(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);

  const canUpdate = can(Permissions.tickets.update);
  const canAssign = can(Permissions.tickets.assign);
  const canChangeStatus = can(Permissions.tickets.changeStatus);
  const canReadCategories = can(Permissions.categories.view);
  const canReadAi = can(Permissions.tickets.ai.read);
  const canGenerateAi = can(Permissions.tickets.ai.generate);
  const isTerminal = ticket.status === TicketStatus.Closed || ticket.status === TicketStatus.Cancelled;

  const ticketQueryKey = ["tickets", "detail", ticket.id] as const;
  const timelineQueryKey = ["tickets", "timeline", ticket.id] as const;
  const aiQueryKey = ["tickets", ticket.id, "ai", "classification"] as const;

  const categoriesQuery = useQuery({
    queryKey: ["categories", "ticket-options", ticket.projectId],
    queryFn: ({ signal }) => categoryApi.getList({ maxResultCount: 1000, sorting: "Path" }, signal),
    enabled: canReadCategories,
    staleTime: 5 * 60 * 1000,
  });
  const categoryOptions = (categoriesQuery.data?.items ?? []).filter((category) => category.projectId === null || category.projectId === ticket.projectId);
  const categoryById = new Map(categoryOptions.map((category) => [category.id, category]));

  const suggestionQuery = useQuery({
    queryKey: aiQueryKey,
    queryFn: ({ signal }) => aiApi.getCurrentClassification(ticket.id, signal),
    enabled: canReadAi,
    retry: false,
  });
  const classification = parseClassificationResult(suggestionQuery.data);

  /** 属性写入成功后立即同步详情；时间线在后台刷新，不阻塞成功反馈。 */
  const syncTicket = (updated: TicketDto) => {
    queryClient.setQueryData<TicketDetailDto>(ticketQueryKey, (current) => (current ? { ...current, ...updated } : current));
    void queryClient.refetchQueries({ queryKey: timelineQueryKey, type: "active" });
    void queryClient.invalidateQueries({ queryKey: ["tickets", "list"] });
    void queryClient.invalidateQueries({ queryKey: ["tickets", "counts"] });
  };

  const syncAssignment = (updated: TicketDto) => {
    syncTicket(updated);
  };

  /** 属性选择后立即更新详情；服务端拒绝时再恢复写入前的完整快照。 */
  const optimisticallyUpdateTicket = async (patch: Partial<TicketDetailDto>): Promise<OptimisticTicketContext> => {
    await queryClient.cancelQueries({ queryKey: ticketQueryKey });
    const previousTicket = queryClient.getQueryData<TicketDetailDto>(ticketQueryKey);
    queryClient.setQueryData<TicketDetailDto>(ticketQueryKey, (current) => (current ? { ...current, ...patch } : current));
    return { previousTicket };
  };

  const rollbackTicket = (context: OptimisticTicketContext | undefined) => {
    if (context?.previousTicket) queryClient.setQueryData(ticketQueryKey, context.previousTicket);
  };

  const statusMutation = useMutation({
    mutationFn: (targetStatus: TicketStatus) => ticketApi.changeStatus(ticket.id, { targetStatus, concurrencyStamp: ticket.concurrencyStamp }),
    onMutate: (targetStatus) => optimisticallyUpdateTicket({ status: targetStatus }),
    onSuccess: (updated) => {
      syncTicket(updated);
      toast.success(t("tickets.properties.statusUpdated", { status: t(statusPresentation(updated.status).labelKey) }));
    },
    onError: (error, _targetStatus, context) => {
      rollbackTicket(context);
      toast.error(errorMessage(error, t("tickets.properties.statusUpdateError")));
    },
  });

  const priorityMutation = useMutation({
    mutationFn: (priority: TicketPriority) => ticketApi.changePriority(ticket.id, { priority, concurrencyStamp: ticket.concurrencyStamp }),
    onMutate: (priority) => optimisticallyUpdateTicket({ priority }),
    onSuccess: (updated) => {
      syncTicket(updated);
      toast.success(t("tickets.properties.priorityUpdated", { priority: t(priorityPresentation(updated.priority).labelKey) }));
    },
    onError: (error, _priority, context) => {
      rollbackTicket(context);
      toast.error(errorMessage(error, t("tickets.properties.priorityUpdateError")));
    },
  });

  const categoryMutation = useMutation({
    mutationFn: (categoryId: string | null) => ticketApi.changeCategory(ticket.id, { categoryId, concurrencyStamp: ticket.concurrencyStamp }),
    onMutate: (categoryId) => {
      setAppliedAiFeedback(null);
      const category = categoryId ? categoryById.get(categoryId) : undefined;
      return optimisticallyUpdateTicket({
        categoryId,
        categoryName: category?.name ?? null,
        categoryPath: category?.path ?? null,
      });
    },
    onSuccess: (updated) => {
      syncTicket(updated);
      queryClient.setQueryData(aiQueryKey, null);
      toast.success(
        updated.categoryPath ? t("tickets.properties.categoryUpdated", { category: updated.categoryPath }) : t("tickets.properties.categoryRemoved"),
      );
    },
    onError: (error, _categoryId, context) => {
      rollbackTicket(context);
      toast.error(errorMessage(error, t("tickets.properties.categoryUpdateError")));
    },
  });

  const classifyMutation = useMutation({
    mutationFn: () => aiApi.classify(ticket.id, Boolean(suggestionQuery.data)),
    onMutate: () => setAppliedAiFeedback(null),
    onSuccess: (suggestion) => {
      queryClient.setQueryData(aiQueryKey, suggestion);
      toast.success(t("tickets.ai.generatedToast"));
    },
    onError: (error) => toast.error(errorMessage(error, t("tickets.ai.generateError"))),
  });

  const applySuggestionMutation = useMutation({
    mutationFn: ({ candidate }: { candidate: ClassificationCategoryCandidate }) =>
      aiApi.applyCategory(suggestionQuery.data?.id ?? "", candidate.categoryId, ticket.concurrencyStamp),
    onMutate: ({ candidate }) => {
      setAppliedAiFeedback(null);
      const category = categoryById.get(candidate.categoryId);
      return optimisticallyUpdateTicket({
        categoryId: candidate.categoryId,
        categoryName: category?.name ?? null,
        categoryPath: category?.path ?? null,
      });
    },
    onSuccess: (updated, { candidate }) => {
      syncTicket(updated);
      queryClient.setQueryData(aiQueryKey, null);
      setAppliedAiFeedback({ confidence: candidate.confidence });
      toast.success(
        t("tickets.ai.appliedToast", {
          category: updated.categoryPath ?? updated.categoryName ?? t("tickets.category.uncategorized"),
        }),
      );
    },
    onError: (error, _variables, context) => {
      rollbackTicket(context);
      toast.error(errorMessage(error, t("tickets.ai.applyError")));
    },
  });

  useEffect(() => {
    if (!appliedAiFeedback) return;
    const timeout = window.setTimeout(() => setAppliedAiFeedback(null), 3200);
    return () => window.clearTimeout(timeout);
  }, [appliedAiFeedback]);

  const aiProgressMessage = useAiProgressMessage(classifyMutation.isPending);
  const participants = ticket.participants ?? [];
  const visibleParticipants = participants.slice(0, MAX_VISIBLE_PARTICIPANTS);
  const hiddenParticipantCount = Math.max(participants.length - visibleParticipants.length, 0);
  const statusOptions = [ticket.status, ...(STATUS_TRANSITIONS[ticket.status] ?? [])];
  const hasCategory = Boolean(ticket.categoryId);
  const isPropertyUpdatePending = statusMutation.isPending || priorityMutation.isPending || categoryMutation.isPending || applySuggestionMutation.isPending;
  const shouldKeepEditableProperties = !isTerminal || statusMutation.isPending;
  const canModifyCategory = canUpdate && !isTerminal;
  const canGenerateCategorySuggestion = canGenerateAi && !isTerminal;
  const isCategoryAiBusy = classifyMutation.isPending || applySuggestionMutation.isPending;
  const showAppliedCategoryState = Boolean(appliedAiFeedback);
  const currentStatusPresentation = statusPresentation(ticket.status);
  const currentPriorityPresentation = priorityPresentation(ticket.priority);

  const categoryAction =
    canReadAi && hasCategory ? (
      <AiCategoryLabelAction
        appliedFeedback={appliedAiFeedback}
        canGenerate={canGenerateCategorySuggestion}
        isApplying={applySuggestionMutation.isPending}
        isGenerating={classifyMutation.isPending}
        isLoading={suggestionQuery.isPending}
        progressMessage={aiProgressMessage}
        onGenerate={() => classifyMutation.mutate()}
      />
    ) : null;

  return (
    <div className="mx-auto max-w-3xl overflow-hidden px-3">
      <div className="space-y-6">
        <TicketPropertiesSection icon="tabler:ticket" label={t("tickets.properties.state")}>
          <TicketPropertiesGrid>
            <TicketPropertiesGridRow label={t("tickets.fields.priority")}>
              {canUpdate ? (
                <Select
                  disabled={isPropertyUpdatePending || isTerminal}
                  value={String(ticket.priority)}
                  onValueChange={(value) => priorityMutation.mutate(Number(value) as TicketPriority)}
                >
                  <SelectTrigger
                    aria-label={t("tickets.fields.priority")}
                    className={PROPERTY_SELECT_TRIGGER_CLASS}
                    loading={priorityMutation.isPending}
                    loadingLabel={t("tickets.properties.priorityUpdating")}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_PRIORITY_VALUES.map((value) => {
                      const { labelKey, icon } = priorityPresentation(value);
                      return (
                        <SelectItem key={value} value={String(value)}>
                          <Icon className="size-3" icon={icon} />
                          {t(labelKey)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <p
                  className={cn(
                    "flex min-h-10 items-center gap-1.5 rounded-lg bg-muted/70 px-3 py-2 text-sm font-medium",
                    currentPriorityPresentation.className,
                  )}
                >
                  <Icon className="size-4" icon={currentPriorityPresentation.icon} />
                  {t(currentPriorityPresentation.labelKey)}
                </p>
              )}
            </TicketPropertiesGridRow>

            <TicketPropertiesGridRow label={t("tickets.fields.status")}>
              {canChangeStatus && (statusOptions.length > 1 || statusMutation.isPending) ? (
                <Select
                  disabled={isPropertyUpdatePending}
                  value={String(ticket.status)}
                  onValueChange={(value) => {
                    const targetStatus = Number(value) as TicketStatus;
                    if (targetStatus === TicketStatus.Resolved) onResolve();
                    else statusMutation.mutate(targetStatus);
                  }}
                >
                  <SelectTrigger
                    aria-label={t("tickets.fields.status")}
                    className={PROPERTY_SELECT_TRIGGER_CLASS}
                    loading={statusMutation.isPending}
                    loadingLabel={t("tickets.properties.statusUpdating")}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((value) => (
                      <SelectItem key={value} value={String(value)}>
                        {t(statusPresentation(value).labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className={cn("flex min-h-10 items-center rounded-lg px-3 py-2 text-sm font-medium", currentStatusPresentation.className)}>
                  {t(currentStatusPresentation.labelKey)}
                </p>
              )}
            </TicketPropertiesGridRow>
          </TicketPropertiesGrid>
        </TicketPropertiesSection>

        <TicketPropertiesSection icon="tabler:tag" label={t("tickets.fields.category")} action={categoryAction}>
          {canUpdate && canReadCategories && shouldKeepEditableProperties ? (
            <CategoryTreeSelect
              categories={categoryOptions}
              disabled={isPropertyUpdatePending || categoriesQuery.isPending || isCategoryAiBusy}
              triggerClassName={cn(
                PROPERTY_SELECT_TRIGGER_CLASS,
                classifyMutation.isPending && "ticket-ai-category-scanning",
                applySuggestionMutation.isPending && "ticket-ai-category-applying",
                showAppliedCategoryState && "ticket-ai-category-applied",
              )}
              triggerIcon={
                classifyMutation.isPending || applySuggestionMutation.isPending || categoryMutation.isPending ? (
                  <Loader
                    variant="metaballs"
                    className="shrink-0 text-primary"
                    label={t(
                      classifyMutation.isPending
                        ? "tickets.ai.generating"
                        : applySuggestionMutation.isPending
                          ? "tickets.ai.applyingCategory"
                          : "tickets.properties.categoryUpdating",
                    )}
                    size={14}
                  />
                ) : showAppliedCategoryState ? (
                  <Icon aria-hidden="true" className="size-4 text-primary" icon="tabler:check" />
                ) : undefined
              }
              value={ticket.categoryId}
              valueLabel={ticket.categoryPath ?? ticket.categoryName}
              onValueChange={(value) => categoryMutation.mutate(value)}
            />
          ) : (
            <div
              className={cn(
                "min-h-10 rounded-lg bg-muted/70 px-3 py-2 text-sm",
                classifyMutation.isPending && "ticket-ai-category-scanning",
                applySuggestionMutation.isPending && "ticket-ai-category-applying",
                showAppliedCategoryState && "ticket-ai-category-applied",
              )}
            >
              {ticket.categoryPath ?? ticket.categoryName ?? t("tickets.category.uncategorized")}
            </div>
          )}

          {canReadAi ? (
            <TicketAiClassification
              applyingCategoryId={applySuggestionMutation.variables?.candidate.categoryId ?? null}
              canApply={canModifyCategory}
              canGenerate={canGenerateCategorySuggestion}
              categoryById={categoryById}
              classification={classification}
              currentCategoryId={ticket.categoryId}
              hasCategory={hasCategory}
              isApplying={applySuggestionMutation.isPending}
              isGenerating={classifyMutation.isPending}
              isLoading={suggestionQuery.isPending}
              key={suggestionQuery.data?.id ?? "empty"}
              latestGeneratedSuggestionId={classifyMutation.data?.id ?? null}
              progressMessage={aiProgressMessage}
              suggestionId={suggestionQuery.data?.id ?? null}
              onApply={(candidate) => applySuggestionMutation.mutate({ candidate })}
              onGenerate={() => classifyMutation.mutate()}
            />
          ) : null}
        </TicketPropertiesSection>

        <TicketPropertiesSection
          icon="tabler:user-shield"
          label={t("tickets.properties.followUp")}
          action={
            canAssign ? (
              <Button
                aria-label={t("tickets.assignment.openDialogAria")}
                disabled={isPropertyUpdatePending}
                className="h-5 px-1.5 text-[11px] font-normal text-muted-foreground hover:bg-transparent hover:text-primary"
                size="xs"
                type="button"
                variant="ghost"
                onClick={() => setAssignmentDialogOpen(true)}
              >
                <Icon icon="tabler:users-plus" />
                {t("tickets.actions.assign")}
              </Button>
            ) : null
          }
        >
          <TicketFollowUpAssignment assignee={ticket.assignee} teamName={ticket.assignedTeamName} />
        </TicketPropertiesSection>

        {canAssign ? (
          <TicketAssignmentDialog open={assignmentDialogOpen} ticket={ticket} onAssigned={syncAssignment} onOpenChange={setAssignmentDialogOpen} />
        ) : null}

        <TicketPropertiesSection icon="ci:users" label={t("tickets.fields.participants")}>
          {participants.length > 0 ? (
            <AvatarGroup aria-label={t("tickets.participants.countAria", { count: new Intl.NumberFormat(intlLocale).format(participants.length) })}>
              {visibleParticipants.map((participant) => (
                <TooltipProvider key={participant.agentId}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Avatar title={participant.displayName}>
                        {participant.avatarUrl ? <AvatarImage alt={participant.displayName} src={participant.avatarUrl} /> : null}
                        <AvatarFallback>{participant.displayName.slice(0, 1)}</AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent>{participant.displayName}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
              {hiddenParticipantCount > 0 ? (
                <AvatarGroupCount
                  aria-label={t("tickets.participants.hiddenCountAria", { count: new Intl.NumberFormat(intlLocale).format(hiddenParticipantCount) })}
                >
                  +{new Intl.NumberFormat(intlLocale).format(hiddenParticipantCount)}
                </AvatarGroupCount>
              ) : null}
            </AvatarGroup>
          ) : (
            <EmptyValue>{t("tickets.participants.empty")}</EmptyValue>
          )}
        </TicketPropertiesSection>

        <TicketPropertiesSection
          icon="fa7-regular:contact-card"
          label={t("tickets.fields.requesterSnapshot")}
          action={
            ticket.customer && customerDetailHref && can(Permissions.customers.view) ? (
              <Button
                asChild
                className="h-5 px-1.5 text-[11px] font-normal text-muted-foreground hover:bg-transparent hover:text-primary"
                size="xs"
                variant="ghost"
              >
                <Link state={{ returnByHistory: true, returnLabelKey: "customers.detail.returnToTicket" }} to={customerDetailHref}>
                  <Icon icon="tabler:user-search" />
                  {t("tickets.customer.viewDetails")}
                </Link>
              </Button>
            ) : null
          }
        >
          {ticket.requester ? (
            <ContactDetails email={ticket.requester.email} name={ticket.requester.name} phone={ticket.requester.phone} />
          ) : (
            <EmptyValue>{t("tickets.customer.empty")}</EmptyValue>
          )}
        </TicketPropertiesSection>

        {extendedInfo.length > 0 ? (
          <TicketPropertiesSection icon="tabler:list-details" label={t("tickets.fields.ticketExtendedInfo")}>
            {/* 业务系统建单时透传、之后不可改；没有内容就整块不出现，属性栏已经够长了。 */}
            <ExtendedInfoList entries={extendedInfo} border={false} />
          </TicketPropertiesSection>
        ) : null}

        <TicketPropertiesSection icon="boxicons:alarm-alt" label="SLA">
          <TicketPropertiesGrid className="gap-y-1">
            <TicketPropertiesGridRow label={t("tickets.fields.creationTime")}>
              <TimeValue value={ticket.creationTime} />
            </TicketPropertiesGridRow>
            <TicketPropertiesGridRow label={t("tickets.fields.firstResponseAt")}>
              <TimeValue value={ticket.firstResponseAt} />
            </TicketPropertiesGridRow>
            <TicketPropertiesGridRow label={t("tickets.fields.resolvedAt")}>
              <TimeValue value={ticket.resolvedAt} />
            </TicketPropertiesGridRow>
            {ticket.closedAt ? (
              <TicketPropertiesGridRow label={t("tickets.fields.closedAt")}>
                <TimeValue value={ticket.closedAt} />
              </TicketPropertiesGridRow>
            ) : null}
          </TicketPropertiesGrid>
        </TicketPropertiesSection>
      </div>
    </div>
  );
}

function EmptyValue({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function ContactDetails({ email, name, phone }: { email: string | null; name: string | null; phone: string | null }) {
  const { t } = useI18n();

  return (
    <div className="space-y-2">
      <p className="min-w-0 truncate text-sm font-medium">{name ?? t("tickets.customer.unnamed")}</p>
      {phone ? <ContactRow href={`tel:${phone}`} icon="tabler:phone" label={phone} /> : null}
      {email ? <ContactRow href={`mailto:${email}`} icon="tabler:mail" label={email} /> : null}
      {!phone && !email ? <p className="text-xs text-muted-foreground">{t("tickets.customer.noContact")}</p> : null}
    </div>
  );
}

function ContactRow({ href, icon, label }: { href: string; icon: string; label: string }) {
  return (
    <a className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground hover:text-foreground" href={href}>
      <Icon className="size-3.5 shrink-0" icon={icon} />
      <span className="truncate">{label}</span>
    </a>
  );
}

function TimeValue({ value, className }: { value: string | null; className?: string }) {
  const { intlLocale } = useI18n();
  return <p className={cn("text-sm tabular-nums text-muted-foreground", className)}>{formatDateTime(value, intlLocale)}</p>;
}
